<template>
  <form name="create-account" @submit.prevent="registerUser" class="create-account-form" ref="registerForm">
    <div class="create-account-form__group create-account-form__group--half">
      <label class="create-account-form__group-label" for="first-name">First Name</label>
      <input
        class="create-account-form__group-input"
        type="text"
        id="first-name"
        name="first-name"
        v-model="firstName"
        autocomplete="given-name"
        placeholder="First Name *"
        required />
    </div>
    <div class="create-account-form__group create-account-form__group--half">
      <label class="create-account-form__group-label" for="last-name">Last Name</label>
      <input
        type="text"
        id="last-name"
        name="last-name"
        v-model="lastName"
        autocomplete="family-name"
        class="create-account-form__group-input"
        placeholder="Last Name *"
        required />
    </div>
    <div class="create-account-form__group">
      <label class="create-account-form__group-label" for="email">Email</label>
      <input
        type="email"
        id="email"
        name="email"
        v-model="email"
        autocomplete="email"
        class="create-account-form__group-input"
        placeholder="Email Address *"
        required />
    </div>
    <div class="create-account-form__group">
      <label class="create-account-form__group-label" for="password">Password</label>
      <input
        type="password"
        id="password"
        autocomplete="new-password"
        v-model="password"
        class="create-account-form__group-input"
        placeholder="Password *"
        required />
    </div>
    <div class="create-account-form__group">
      <label class="create-account-form__group-label" for="password-repeat">Password</label>
      <input
        type="password"
        id="password-repeat"
        autocomplete="new-password"
        v-model="passwordRepeat"
        class="create-account-form__group-input"
        placeholder="Confirm Password *"
        required />
    </div>
    <button class="create-account-form__button" type="submit">
      Register
    </button>

    <div v-if="message" :class="['register__message', messageType]">
      {{ message }}
    </div>
  </form>
</template>

<script setup lang="ts">
import { getRecaptchaToken, loadRecaptcha, unmountRecaptcha } from "~/composables/grecaptcha";
import { loginHandler, registrationHandler } from "~/utils/session";
import {
  onMounted,
  onUnmounted,
  ref,
  navigateTo
} from "#imports";

const props = defineProps({
  emitEvent: Boolean,
});

const emit = defineEmits(['login-successful']);

const email = ref('');
const password = ref('');
const passwordRepeat = ref('');
const firstName = ref('');
const lastName = ref('');
const messageType = ref('');
const message = ref('');
const submitStatus = ref(0);

onMounted(() => {
  loadRecaptcha();
});

onUnmounted(() => {
  unmountRecaptcha();
})

const registerUser = async () => {
  if (submitStatus.value === 1 || (password.value && password.value !== passwordRepeat.value)) {
    message.value = 'Passwords need to be identical'
    messageType.value = 'error'
    return;
  }

  submitStatus.value = 1;
  const token = await getRecaptchaToken()

  const result = await registrationHandler({
    firstName: firstName.value,
    lastName: lastName.value,
    email: email.value,
    password: password.value,
    captchaToken: token
  })

  if (result?.data?.registerCustomer?.viewer?.id) {
    message.value = 'Account registered'
    messageType.value = 'success'

    handleLogin();
    return;
  }

  let errorMessage = 'There was an error. Please try again.'

  if (result?.errors) {
    errorMessage = result.errors[0].message
  }

  message.value = errorMessage
  messageType.value = 'error'
};

const handleLogin = async () => {
  const token = await getRecaptchaToken()

  const result = await loginHandler({ username: email.value, password: password.value, captchaToken: token });

  if (result) {
    if (props.emitEvent) {
      emit('login-successful');
    } else {
      await navigateTo('/my-account/');
    }
  } else {
    message.value = 'There was an error on login. Please try again or contact support.';
    messageType.value = 'error';
  }
}
</script>

<style lang="scss">
@use "~/common/scss/form";

.create-account-form {
  @include form.baseForm('__');
}
</style>
